import React, { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import {
  Box,
  useToast,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Heading,
  Text,
  HStack,
  VStack,
  Divider,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { ExamManualAttemptsByTutor, ExamManualAttemptDetails, ExamManualAttemptDetailsEval, ExamAttemptScoreStatus } from "../Common/types";
import ExamAttemptManualEvaluator from "../Components/ExamAttemptManualEvaluator";
import { toUtcISOString, parseUtc, fmtLocal } from "../../utils/dates";

interface TokenPayload {
  sub: string;
  role_id: number;
  tenant_id: number;
  exp?: number;
}

const EvaluateExamByTutor = () => {
  const { exam_id } = useParams<{ exam_id: string }>();
  const examId = Number(exam_id);
  const toast = useToast();

  const [examManualAttemptsByTutor, setExamManualAttemptsByTutor] = useState<ExamManualAttemptsByTutor | null>(null);

  const [examManualAttemptDetails, setExamManualAttemptDetails] = useState<ExamManualAttemptDetails | null>(null);

  const [activeAttemptId, setActiveAttemptId] = useState<number | null>(null);

  const [loading, setLoading] = useState<boolean>(true);

  const cardBg = useColorModeValue("gray.50", "gray.700");
  const contextBg = useColorModeValue("blue.50", "blue.900");

  const fetchExamManualAttemptsByTutor = async () => {
    try {
      const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
      const { data } = await axios.get<ExamManualAttemptsByTutor>(
        `${api}/tutor/exam/${exam_id}/manual-exam-attempts/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setExamManualAttemptsByTutor(data);
    } catch (err) {
      console.error(err);
      toast({ title: "Error fetching exam attempts", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSave = async (manualScoringData: ExamManualAttemptDetailsEval) => {
  await saveManualAttemptToAPI(manualScoringData);
  // remove attempt from state / accordion
 // setExamManualAttemptsByTutor(prev => prev.filter(a => a.exam_attempt_id !== manualScoringData.exam_attempt_id));

  setExamManualAttemptsByTutor(prev => {
    if (!prev) return prev; // or `return null;` if you prefer a hard fallback

    return {
      ...prev,
      exam_attempts: prev.exam_attempts.filter(
        a => a.exam_attempt_id !== manualScoringData.exam_attempt_id
      )
    };
  });

};

 const saveManualAttemptToAPI = async (manualScoringData : ExamManualAttemptDetailsEval) => {

    try {
  
      const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
  
      const { data } = await axios.post<ExamAttemptScoreStatus>(`${api}/exam/scoring/manual-attempt/${manualScoringData.exam_attempt_id}`, manualScoringData, {  //axios.post(`${api}/scoring/execute/${exam_id}`, exam, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
  
      alert("Manual evaluation saved successfully!");
      toast({ title: "Manual evaluation saved successfully!", status: "success" });
    } catch (error) {
      console.error("Error saving evaluation:", error);
      toast({ title: "Error saving evaluation:", status: "error" });
    }
  };


  const fetchExamManualAttempt = async (exam_attempt_id : number) => {
    try {
      const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
      const { data } = await axios.get<ExamManualAttemptDetails>(
      
        `${api}/exam/attempt/${exam_attempt_id}/manual-question-details`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setExamManualAttemptDetails(data);
    } catch (err) {
      console.error(err);
      toast({ title: "Error fetching exam attempts", status: "error" });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchExamManualAttemptsByTutor();
  }, [examId]);

  return (
    <Box as="main" mx="auto" p={4} >
      {/* Context Band */}
      {examManualAttemptsByTutor && (
        <HStack
          bg={contextBg}
          p={4}
          borderRadius="md"
          justifyContent="space-between"
          spacing={8}
          mb={6}
        >
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">{examManualAttemptsByTutor.exam_title}</Heading>
            <Text fontSize="sm">
              {fmtLocal(examManualAttemptsByTutor.exam_start_time)} →{" "}
              {fmtLocal(examManualAttemptsByTutor.exam_end_time)}
      
            </Text>
          </VStack>

          <Box>
            <Text fontSize="sm">
              <strong>Tutor:</strong> {examManualAttemptsByTutor.tutor_name}
            </Text>
          </Box>
        </HStack>
      )}

      {/* Spinner while loading */}
      {loading && (
        <Box textAlign="center" py={10}>
          <Spinner size="xl" color="blue.500" />
        </Box>
      )}

      {!loading && examManualAttemptsByTutor && examManualAttemptsByTutor.exam_attempts.length > 0 ? (
        <Accordion
          allowToggle
          onChange={(index) => {
            const openedId =
              typeof index === "number"
                ? examManualAttemptsByTutor.exam_attempts[index]?.exam_attempt_id
                : null;

            setActiveAttemptId(openedId);
            if (openedId) {
              fetchExamManualAttempt(openedId);
            } else {
              setExamManualAttemptDetails(null);
            }
          }}
        >
          {examManualAttemptsByTutor.exam_attempts.map((attempt) => (
            <AccordionItem key={attempt.exam_attempt_id} bg={cardBg} borderRadius="md" mb={3}>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="semibold">
                      Attempt ID: {attempt.exam_attempt_id} • Student: {attempt.student_name}
                    </Text>
                  </Box>
                  <Badge colorScheme={attempt.manual_scoring_completed ? "green" : "orange"} mr={4}>
                    {attempt.manual_scoring_completed ? "Scored" : "Pending"}
                  </Badge>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel>
                {activeAttemptId === attempt.exam_attempt_id && examManualAttemptDetails &&
                examManualAttemptDetails.exam_attempt_id === attempt.exam_attempt_id ? (
                  <ExamAttemptManualEvaluator examManualAttemptDetails={examManualAttemptDetails} onSubmit={handleManualSave} />
                ) : (
                  <Text>Loading details...</Text>
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        !loading && (
          <Box textAlign="center" mt={8}>
            <Text>No pending manual exam attempts found for this exam and tutor.</Text>
          </Box>
        )
      )}
    </Box>
  );
};

export default EvaluateExamByTutor;
